/* ═══════════════════════════════════════════════════════════════
   Material-Gated Install Scheduling — ESID Engine
   ═══════════════════════════════════════════════════════════════
   Single canonical resolver: given a job, what is the
   Earliest Safe Install Date (ESID) and why?

   Inputs (resolved by callers, passed in):
     - Purchase orders for the job
     - Ordering packet line items (criticality, ETA per line)
     - Vendor reliability scores (0..1, higher = more on-time)
     - Policy (buffer tiers, mode)

   Output: MaterialGateVerdict — drives banner, calendar blackout,
   auto-tentative scheduling, and override flow.

   This module is PURE — no React, no context, no side effects.
   Hooks live in `useMaterialGate.ts`.
   ═══════════════════════════════════════════════════════════════ */

import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { OrderingPacket, OrderingLineItem } from "@/data/orderingPacket/types";

/* ─── Criticality ─────────────────────────────────────────── */

export type ItemCriticality = "critical" | "standard" | "commodity";

/**
 * Categories that block install if missing (critical-path).
 * If category isn't in this set, item defaults to "standard".
 * Commodity items (caulk, screws) can be field-purchased.
 */
const CRITICAL_CATEGORIES = new Set([
  "wall_panel", "wall panel", "panels",
  "door", "doors", "shower_door", "glass",
  "tub", "base", "shower_base", "pan",
  "fixture_primary", // main valve / diverter
]);

const COMMODITY_CATEGORIES = new Set([
  "caulk", "sealant", "screws", "fasteners", "shims", "consumable",
]);

export function classifyCriticality(item: OrderingLineItem): ItemCriticality {
  const cat = (item.category ?? "").toLowerCase();
  if (CRITICAL_CATEGORIES.has(cat)) return "critical";
  if (COMMODITY_CATEGORIES.has(cat)) return "commodity";
  return "standard";
}

/* ─── Policy ──────────────────────────────────────────────── */

/** How auto-tentative scheduling behaves once all POs are placed. */
export type AutoTentativeMode =
  | "auto"      // place tentative slot at ESID immediately
  | "suggest"   // only show suggestion, dispatcher books manually
  | "hybrid";   // auto for trusted vendors, suggest otherwise

export interface MaterialGatePolicy {
  /** Buffer in business days added on top of latest confirmed ETA. */
  bufferDaysCritical: number;   // default 3
  bufferDaysStandard: number;   // default 2
  bufferDaysCommodity: number;  // default 1
  /** Extra buffer added when vendor reliability is below this threshold. */
  vendorReliabilityThreshold: number; // default 0.85
  vendorReliabilityPenaltyDays: number; // default 1
  /** Auto-tentative scheduling mode. */
  autoTentativeMode: AutoTentativeMode; // default "auto"
  /** Reliability cutoff for hybrid mode. */
  hybridTrustedThreshold: number; // default 0.9
}

export const DEFAULT_MATERIAL_GATE_POLICY: MaterialGatePolicy = {
  bufferDaysCritical: 3,
  bufferDaysStandard: 2,
  bufferDaysCommodity: 1,
  vendorReliabilityThreshold: 0.85,
  vendorReliabilityPenaltyDays: 1,
  autoTentativeMode: "auto",
  hybridTrustedThreshold: 0.9,
};

/* ─── Verdict ─────────────────────────────────────────────── */

export type MaterialGateState =
  | "no_orders"        // nothing ordered yet
  | "drafting"         // POs exist but not all submitted
  | "awaiting_eta"     // submitted but no ETAs yet
  | "gated"            // ETAs known, install must wait
  | "partial_received" // some received, others outstanding
  | "ready"            // all critical received, install can move
  | "blocked";         // critical items in exception/backorder

export interface MaterialGateLineSummary {
  poId: string;
  poNumber: string;
  vendorName: string;
  productName: string;
  criticality: ItemCriticality;
  etaIso?: string;
  etaConfirmed: boolean;
  received: boolean;
  exception?: string;
}

export interface MaterialGateVerdict {
  state: MaterialGateState;
  /** Earliest Safe Install Date (ISO yyyy-mm-dd) or null if unknown. */
  esidIso: string | null;
  /** ETA driver: the latest expected delivery (without buffer). */
  driverEtaIso: string | null;
  driverVendor?: string;
  driverProduct?: string;
  /** Buffer days actually applied (after vendor reliability penalty). */
  bufferDaysApplied: number;
  /** True when all critical items received → install can ignore date gate. */
  allCriticalReceived: boolean;
  /** Lines outstanding (not received, not exception-resolved). */
  outstandingLines: MaterialGateLineSummary[];
  /** Lines blocking ready state (critical & not received & no ETA / exception). */
  blockingLines: MaterialGateLineSummary[];
  /** Human-readable explanation for banner. */
  reason: string;
}

/* ─── Helpers ─────────────────────────────────────────────── */

function addBusinessDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00");
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

function maxIso(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/* ─── Engine ──────────────────────────────────────────────── */

export interface ResolveMaterialGateInput {
  jobId: string;
  purchaseOrders: PurchaseOrder[];
  packet?: OrderingPacket;
  /** vendorName -> 0..1 reliability score. Default 0.9 if not present. */
  vendorReliability: Record<string, number>;
  policy?: MaterialGatePolicy;
}

export function resolveMaterialGate(input: ResolveMaterialGateInput): MaterialGateVerdict {
  const policy = input.policy ?? DEFAULT_MATERIAL_GATE_POLICY;
  const jobPOs = input.purchaseOrders.filter(p => p.jobId === input.jobId);

  // No orders yet → nothing to gate.
  if (jobPOs.length === 0) {
    return {
      state: "no_orders",
      esidIso: null,
      driverEtaIso: null,
      bufferDaysApplied: 0,
      allCriticalReceived: false,
      outstandingLines: [],
      blockingLines: [],
      reason: "No purchase orders placed yet for this job.",
    };
  }

  // All POs still draft → drafting.
  const anySubmitted = jobPOs.some(p => p.status !== "Draft");
  if (!anySubmitted) {
    return {
      state: "drafting",
      esidIso: null,
      driverEtaIso: null,
      bufferDaysApplied: 0,
      allCriticalReceived: false,
      outstandingLines: [],
      blockingLines: [],
      reason: "Purchase orders are still being drafted.",
    };
  }

  // Build line summaries — prefer packet (has criticality + per-line ETA).
  const packetLines = input.packet?.lineItems ?? [];
  const summaries: MaterialGateLineSummary[] = [];

  for (const po of jobPOs) {
    // Find matching packet lines for this PO.
    const linesForPo = packetLines.filter(l => l.poId === po.poId);
    if (linesForPo.length === 0) {
      // Fallback: synthesize from PO items, no criticality info → standard.
      for (const item of po.items) {
        summaries.push({
          poId: po.poId,
          poNumber: po.poNumber,
          vendorName: po.vendorName,
          productName: item.productName,
          criticality: "standard",
          etaIso: undefined,
          etaConfirmed: po.status === "Confirmed" || po.status === "Delivered",
          received: po.status === "Delivered",
        });
      }
      continue;
    }
    for (const line of linesForPo) {
      const exception = line.exceptions.find(e => !e.resolved);
      summaries.push({
        poId: po.poId,
        poNumber: po.poNumber,
        vendorName: line.actualVendor ?? line.projectedVendor ?? po.vendorName,
        productName: line.actualItemName ?? line.projectedItemName,
        criticality: classifyCriticality(line),
        etaIso: line.expectedDeliveryDate,
        etaConfirmed: po.status === "Confirmed" || po.status === "Delivered",
        received: !!line.receivedAt,
        exception: exception?.detail,
      });
    }
  }

  const outstanding = summaries.filter(s => !s.received);
  const criticalOutstanding = outstanding.filter(s => s.criticality === "critical");
  const allCriticalReceived = criticalOutstanding.length === 0;

  // Check for blocking exceptions on critical items.
  const blockingLines = criticalOutstanding.filter(
    s => s.exception || (!s.etaIso && s.etaConfirmed)
  );

  // ETA-less submitted POs → awaiting ETA.
  const knownEtas = outstanding.filter(s => s.etaIso);
  if (outstanding.length > 0 && knownEtas.length === 0 && !allCriticalReceived) {
    return {
      state: "awaiting_eta",
      esidIso: null,
      driverEtaIso: null,
      bufferDaysApplied: 0,
      allCriticalReceived,
      outstandingLines: outstanding,
      blockingLines,
      reason: "Waiting on vendor ETAs to set the safe install date.",
    };
  }

  if (blockingLines.length > 0) {
    return {
      state: "blocked",
      esidIso: null,
      driverEtaIso: null,
      bufferDaysApplied: 0,
      allCriticalReceived: false,
      outstandingLines: outstanding,
      blockingLines,
      reason: `${blockingLines.length} critical item${blockingLines.length === 1 ? "" : "s"} on backorder or exception — resolve before scheduling.`,
    };
  }

  // Compute ESID per line: line.eta + buffer(line.criticality) + reliability penalty.
  let driverEta: string | null = null;
  let driverVendor: string | undefined;
  let driverProduct: string | undefined;
  let esid: string | null = null;
  let bufferApplied = 0;

  for (const s of outstanding) {
    if (!s.etaIso) continue;
    const baseBuffer =
      s.criticality === "critical" ? policy.bufferDaysCritical
      : s.criticality === "commodity" ? policy.bufferDaysCommodity
      : policy.bufferDaysStandard;
    const reliability = input.vendorReliability[s.vendorName] ?? 0.9;
    const penalty = reliability < policy.vendorReliabilityThreshold
      ? policy.vendorReliabilityPenaltyDays : 0;
    const totalBuffer = baseBuffer + penalty;
    const lineEsid = addBusinessDays(s.etaIso, totalBuffer);

    if (!esid || lineEsid > esid) {
      esid = lineEsid;
      driverEta = s.etaIso;
      driverVendor = s.vendorName;
      driverProduct = s.productName;
      bufferApplied = totalBuffer;
    }
  }

  if (allCriticalReceived) {
    return {
      state: outstanding.length === 0 ? "ready" : "partial_received",
      esidIso: null, // gate dissolved
      driverEtaIso: null,
      bufferDaysApplied: 0,
      allCriticalReceived: true,
      outstandingLines: outstanding,
      blockingLines: [],
      reason: outstanding.length === 0
        ? "All materials received and counted. Install can be scheduled."
        : `All critical materials received. ${outstanding.length} non-critical item${outstanding.length === 1 ? "" : "s"} pending — manager may release.`,
    };
  }

  if (!esid) {
    return {
      state: "awaiting_eta",
      esidIso: null,
      driverEtaIso: null,
      bufferDaysApplied: 0,
      allCriticalReceived,
      outstandingLines: outstanding,
      blockingLines,
      reason: "Waiting on vendor ETAs to set the safe install date.",
    };
  }

  return {
    state: "gated",
    esidIso: esid,
    driverEtaIso: driverEta,
    driverVendor,
    driverProduct,
    bufferDaysApplied: bufferApplied,
    allCriticalReceived: false,
    outstandingLines: outstanding,
    blockingLines: [],
    reason: `Install no earlier than ${esid}. Driven by ${driverVendor ?? "vendor"} (${driverProduct ?? "item"}) — expected ${driverEta}, +${bufferApplied} business day${bufferApplied === 1 ? "" : "s"} buffer.`,
  };
}

/* ─── Auto-tentative decision ─────────────────────────────── */

export function shouldAutoPlaceTentative(
  verdict: MaterialGateVerdict,
  policy: MaterialGatePolicy,
  vendorReliability: Record<string, number>,
): boolean {
  if (verdict.state !== "gated" || !verdict.esidIso) return false;
  if (policy.autoTentativeMode === "suggest") return false;
  if (policy.autoTentativeMode === "auto") return true;
  // hybrid: only if driver vendor is trusted
  const r = verdict.driverVendor
    ? (vendorReliability[verdict.driverVendor] ?? 0)
    : 0;
  return r >= policy.hybridTrustedThreshold;
}

/* ─── Override authority ──────────────────────────────────── */

export const ESID_OVERRIDE_ROLES = ["ops_manager", "admin", "super_admin", "it_staff"] as const;
export type EsidOverrideRole = typeof ESID_OVERRIDE_ROLES[number];

export function canOverrideEsid(role: string): role is EsidOverrideRole {
  return (ESID_OVERRIDE_ROLES as readonly string[]).includes(role);
}